import DocumentIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Document.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Alert, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { handleErrorToast } from 'src/components/molecules';
import {
  GetMitigationPlanDownloadUrlMutation,
  MitigationPlansForDisclosureQuery,
  useGetMitigationPlanDownloadUrlMutation,
} from 'src/graphql';
import theme from 'src/theme/theme';

export const ApprovedWithoutMitigation = (
  firstMitigationPlan:
    | MitigationPlansForDisclosureQuery['mitigationPlansForDisclosure'][0]
    | null,
) => {
  const { tokens } = useTheme<LensTheme>();
  const getDownloadURL = useGetMitigationPlanDownloadUrlMutation();

  const handleOnViewMitigationDocument = (
    upload: MitigationPlansForDisclosureQuery['mitigationPlansForDisclosure'][0]['uploads'][0],
  ) => {
    getDownloadURL
      .mutateAsync({
        uploadId: upload?.uploadId ?? '',
        planId: firstMitigationPlan?.id ?? '',
      })
      .then(
        ({
          getMitigationPlanDownloadURL: { url: downloadUrl },
        }: GetMitigationPlanDownloadUrlMutation) => {
          window.location.href = downloadUrl;
        },
      )
      .catch((err: unknown) => {
        const errWithIds = err as Error & {
          requestId?: string;
          traceId?: string;
        };
        console.error('MitigationPlanFileViewer: failed to get download URL', {
          uploadId: upload?.uploadId ?? '',
          planId: firstMitigationPlan?.id ?? '',
          message: err instanceof Error ? err.message : String(err),
          requestId: errWithIds.requestId,
          traceId: errWithIds.traceId,
          err,
        });
        handleErrorToast({
          toastMessage: 'Failed to open document',
          toastDescription: 'The file could not be loaded. Please try again.',
        });
      });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[2].value,
        padding: tokens.core.spacing[3].value,
        borderRadius: tokens.semantic.radius.md.value,
        border: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
      }}
    >
      <Typography variant="h3" sx={{ fontWeight: 600 }}>
        Approval outcome
      </Typography>
      <Alert severity="success">
        Approved without a mitigation plan. The disclosure is now closed.
      </Alert>
      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '16px' }}>
        Approval comment
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '14px' }}>
        {firstMitigationPlan?.description}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
        }}
      >
        <Typography
          variant="body2"
          color={theme.atlas.semantic.color.type.muted}
        >
          Reviewer:{' '}
          <strong>{firstMitigationPlan?.createdByName ?? 'N/A'}</strong>
        </Typography>
        <Typography
          variant="body2"
          color={theme.atlas.semantic.color.type.muted}
        >
          •
        </Typography>
        <Typography
          variant="body2"
          color={theme.atlas.semantic.color.type.muted}
        >
          Submitted on:{' '}
          <strong>
            {new Date(
              firstMitigationPlan?.createdAt ?? '',
            ).toLocaleDateString()}
          </strong>
        </Typography>
      </Box>
      {firstMitigationPlan?.uploads &&
        firstMitigationPlan.uploads.length > 0 && (
          <Box
            sx={{
              marginTop: tokens.core.spacing[2].value,
              borderRadius: '8px',
              border: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
              padding: tokens.core.spacing[2].value,
            }}
          >
            {firstMitigationPlan.uploads.map((u) => {
              return (
                <Box
                  data-testid={`document-upload-${u.uploadId}-row`}
                  key={u.uploadId}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:not(:last-child)': {
                      borderBottom: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
                      paddingBottom: tokens.core.spacing[2].value,
                    },
                    '&:not(:first-of-type)': {
                      paddingTop: tokens.core.spacing[2].value,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: theme.atlas.semantic.color.status.neutral.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <DocumentIcon />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.atlas.semantic.color.type.default,
                        fontSize: '16px',
                      }}
                    >
                      {u.fileExtension
                        ? `Attachment ${u.uploadOrder + 1} (${u.fileExtension})`
                        : `Attachment ${u.uploadOrder + 1}`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: tokens.core.spacing[2].value,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => handleOnViewMitigationDocument(u)}
                    >
                      View
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
    </Box>
  );
};
